import {RequestQueryBuilder, SCondition} from '@nestjsx/crud-request';
import {Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../../config/api.configuration';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Burly} from 'kb-burly';
import {ManyResponse} from '../../../../responses/many.response';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {ComponentPainByFleet} from '../../models/component-pain-table-fleet.model';
import {Cacheable} from 'ts-cacheable';
import {ComponentPainByOverall} from '../../models';

@Injectable({
  providedIn: 'root'
})
export class TorchComponentPainFleetService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }


  @Cacheable()
  public queryComponentPainTable(painType: string = '1yr',
                                 pageSize: number = 15,
                                 currentPage: number = 0,
                                 sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
                                 sortBy: string = 'rank',
                                 filters: SCondition | null): Observable<ManyResponse<ComponentPainByFleet>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment(painType)
      .appendRawQueryString(qb.query(false))
      .get;


    return this.httpClient.get<ManyResponse<ComponentPainByFleet>>(endpoint);
  }

  public getPainIndexMonths(painType: string = '1yr'): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment(painType)
      .addSegment('/painIndexMonth')
      .get;

    return this.httpClient.get<[{ pain_index_month: string }]>(endpoint).pipe(
      map(response => response.map(v => v.pain_index_month))
    );
  }

  public getMaxPainIndexMonth(painType: string = '1yr'): Observable<string> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment(painType)
      .addSegment('/maxPainIndexMonth')
      .get;

    return this.httpClient.get<{ pain_index_month: string }>(endpoint).pipe(
      map(response => response.pain_index_month)
    );
  }

  public getDistinctNouns(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment(painType)
      .addSegment('/nouns')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ noun: string }]>(endpoint).pipe(
      map(response => response.map(v => v.noun))
    );
  }

  public getDistinctDescriptions(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment(painType)
      .addSegment('/descriptions')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ description: string }]>(endpoint).pipe(
      map(response => response.map(v => v.description))
    );
  }

  public getDistinctAtaDescriptions(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment(painType)
      .addSegment('/ataDescriptions')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ ATA_DESCRIPTION: string }]>(endpoint).pipe(
      map(response => response.map(v => v.ATA_DESCRIPTION))
    );
  }

  public getDistinctAtaGroups(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment(painType)
      .addSegment('/ataGroups')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ ATA_GROUP: string }]>(endpoint).pipe(
      map(response => response.map(v => v.ATA_GROUP))
    );
  }

  public getDistinctFleets(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment(painType)
      .addSegment('/fleets')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ fleet_cd: string }]>(endpoint).pipe(
      map(response => response.map(v => v.fleet_cd))
    );
  }

  public getTop5CpnsByFleet(fleet: string): Observable<ComponentPainByFleet[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/1yr')
      .addSegment('/top5/fleet/' + fleet)
      .get;

    return this.httpClient.get<ComponentPainByFleet[]>(endpoint).pipe(
      distinctUntilChanged()
    );
  }

  @Cacheable()
  public getPainHistoryByCpnFleet(painType: string = '1yr', cpn: string, fleet: string): Observable<ComponentPainByFleet[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment(painType)
      .addSegment('/history/cpn/' + cpn + '/fleet/' + fleet)
      .get;

    return this.httpClient.get<ComponentPainByFleet[]>(endpoint);
  }

  public getDistinctFleetsByCpn(cpn: string): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet/')
      .addSegment('5yr')
      .addSegment('/list/cpn/' + cpn + '/fleets')
      .get;

    return this.httpClient.get<[{ fleet_cd: string }]>(endpoint).pipe(
      map(response => response.map(v => v.fleet_cd))
    );
  }

  public exportToExcel(painType: string = '1yr', painData: ComponentPainByFleet[]) {
    return new Observable(subscriber => {
      const endpoint = Burly(this.config.rootUrl)
        .addSegment('/pain-index/component/fleet/')
        .addSegment(painType)
        .addSegment('/export')
        .get;

      const httpOptions: { headers; observe; responseType } = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        observe: 'response',
        responseType: 'blob'
      };

      const body = {
        painData
      };
      this.httpClient.post(endpoint, body, httpOptions).subscribe((response) => {
        subscriber.next((response as unknown as HttpResponse<any>).body);
        subscriber.complete();
      });
    });
  }
}
